import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {StateConfigService} from "../../../../services/state/state-config.service";
import {ActivatedRoute, Router} from "@angular/router";
import {LoadingScreenComponent} from "../../../util/loading-screen/loading-screen.component";
import {QualityStepperComponent} from "../quality-stepper/quality-stepper.component";
import {CallToActionBannerComponent} from "../../../util/call-to-action-banner/call-to-action-banner.component";
import {QualityCheck, QualityControllerService, QualityCheckProjectFiles} from "../../../../api";
import {catchError, filter, interval, of, startWith, Subject, switchMap, take, takeUntil} from "rxjs";

@Component({
  selector: 'app-quality-loading',
  templateUrl: './quality-loading.component.html',
  styleUrls: ['./quality-loading.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LoadingScreenComponent,
    QualityStepperComponent,
    CallToActionBannerComponent
  ]
})
export class QualityLoadingComponent implements OnInit, OnDestroy {
  stateConfig = inject(StateConfigService);
  private qualityControllerService = inject(QualityControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  private static readonly POLL_INTERVAL_MS = 2000;
  private destroy$ = new Subject<void>();
  private hasNavigated = false;

  ngOnInit(): void {
    const checkId = this.activatedRoute.snapshot.paramMap.get('checkId');
    if (!checkId) {
      return;
    }

    this.pollCheckCompletion(checkId);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pollCheckCompletion(checkId: string): void {
    interval(QualityLoadingComponent.POLL_INTERVAL_MS).pipe(
      startWith(0),
      switchMap(() => this.qualityControllerService.getCheck(checkId, this.stateConfig.getProjectId())
        .pipe(catchError(() => of(null)))),
      filter((check): check is QualityCheck => !!check && QualityLoadingComponent.isCheckCompleted(check)),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.navigateToReport(checkId);
    });
  }

  private static isCheckCompleted(check: QualityCheck): boolean {
    const projectFiles = check.qualityCheckProjectFiles ?? [];
    return projectFiles.length > 0 && projectFiles.every(file => QualityLoadingComponent.hasResolvedCheckResult(file));
  }

  private static hasResolvedCheckResult(file: QualityCheckProjectFiles): boolean {
    const result = file.check?.result;
    return !!result && Object.keys(result).length > 0;
  }

  private navigateToReport(checkId: string): void {
    if (this.hasNavigated) {
      return;
    }
    this.hasNavigated = true;
    this.router.navigateByUrl('/projects/' + this.stateConfig.getProjectId() + '/quality/' + checkId + '/report');
  }

  protected readonly window = window;
}
